import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "components/Layout";
import api from "utils/api";
import { formatPrice } from "constants/massages";
import "styles/konto.css";

const formatLong = (date) =>
  new Date(date).toLocaleString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const KontoPage = () => {
  const user = useSelector((s) => s.user);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data.bookings || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Buchungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCancel = async (id) => {
    if (!window.confirm("Möchtest du diese Buchung wirklich stornieren?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Stornierung fehlgeschlagen.");
    }
  };

  const upcoming = bookings.filter((b) => new Date(b.start) >= new Date() && b.status !== "cancelled");
  const past = bookings.filter((b) => new Date(b.start) < new Date() || b.status === "cancelled");

  return (
    <Layout>
      <section className="konto-page">
        <div className="konto-shell">
          <header className="konto-header">
            <div>
              <h1>
                Mein <em>Konto</em>
              </h1>
              <p>
                Willkommen{user?.firstName ? `, ${user.firstName}` : ""}. Hier siehst du
                kommende und vergangene Termine, kannst Buchungen ändern oder neue anlegen.
              </p>
            </div>
            <Link to="/buchen" className="btn-primary">
              Neuen Termin buchen
            </Link>
          </header>

          {error && <div className="auth-alert is-error">{error}</div>}

          <h2 style={{ fontFamily: "var(--font-display)", color: "var(--ink)", marginBottom: "1rem" }}>
            Kommende Termine
          </h2>
          {loading ? (
            <div className="konto-empty">Termine werden geladen…</div>
          ) : upcoming.length === 0 ? (
            <div className="konto-empty">
              <h3>Keine kommenden Termine</h3>
              <p>
                Du hast aktuell keine offenen Buchungen.{" "}
                <Link to="/buchen" style={{ color: "var(--dark-blue)", fontWeight: 600 }}>
                  Jetzt buchen →
                </Link>
              </p>
            </div>
          ) : (
            <div className="booking-list">
              {upcoming.map((b) => (
                <article
                  key={b._id}
                  className={`booking-row ${b.status === "cancelled" ? "is-cancelled" : ""}`}
                >
                  <div>
                    <div className="booking-service">{b.serviceLabel}</div>
                    <div className="booking-meta">
                      <span><strong>Termin:</strong> {formatLong(b.start)}</span>
                      <span><strong>Dauer:</strong> {b.durationMinutes} Min.</span>
                      <span>
                        <strong>Preis:</strong>{" "}
                        {b.priceCents === 0 ? "Kostenfrei" : formatPrice(b.priceCents)}
                      </span>
                      <span>
                        <strong>Zahlung:</strong>{" "}
                        {b.paymentMethod === "stripe"
                          ? "Online"
                          : b.paymentMethod === "later"
                            ? "Nach Termin"
                            : "Vor Ort"}
                      </span>
                      <span><strong>Ort:</strong> {b.location}</span>
                    </div>
                  </div>
                  <div className="booking-actions">
                    <Link to={`/konto/buchungen/${b._id}/aendern`}>Termin ändern</Link>
                    {b.paymentMethod === "stripe" && b.paymentStatus === "pending" && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/payment/checkout/${b._id}`);
                            if (data?.url) window.location.href = data.url;
                          } catch (err) {
                            setError(err?.response?.data?.message || "Zahlung nicht möglich.");
                          }
                        }}
                      >
                        Jetzt bezahlen
                      </button>
                    )}
                    <button type="button" className="danger" onClick={() => handleCancel(b._id)}>
                      Stornieren
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {past.length > 0 && (
            <>
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  color: "var(--ink)",
                  margin: "2.5rem 0 1rem",
                }}
              >
                Vergangene Termine
              </h2>
              <div className="booking-list">
                {past.map((b) => (
                  <article
                    key={b._id}
                    className={`booking-row ${b.status === "cancelled" ? "is-cancelled" : ""}`}
                  >
                    <div>
                      <div className="booking-service">{b.serviceLabel}</div>
                      <div className="booking-meta">
                        <span><strong>Termin:</strong> {formatLong(b.start)}</span>
                        <span>
                          <strong>Preis:</strong>{" "}
                          {b.priceCents === 0 ? "Kostenfrei" : formatPrice(b.priceCents)}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default KontoPage;
